// To change portfolio colors globally go to the _globalColor.scss file

import emoji from "react-easy-emoji";
import splashAnimation from "./assets/lottie/satellite.json";

// Splash Screen

const splashScreen = {
  enabled: true, // set false to disable splash screen
  animation: splashAnimation,
  duration: 2000 // Set animation duration as per your animation
};

// Summary And Greeting Section

const illustration = {
  animated: true // Set to false to use static SVG
};

const greeting = {
  username: "Skyfall Space",
  title: "Welcome to Skyfall Space",
  subTitle: emoji(
    "At Skyfall Space, our CLEANS mission is dedicated to making space sustainable. By deploying a cutting‐edge tether net and drag sail deorbit system on a 6U CubeSat, we actively capture and safely deorbit orbital debris—helping to protect satellites, space stations, and future missions."
  ),
  resumeLink: "", // Set to empty to hide the button
  displayGreeting: true // Set false to hide this section, defaults to true
};

// Social Media Links

const socialMediaLinks = {
  linkedin: "https://www.linkedin.com/company/skyfallspace/",
  gmail: "info@skyfallspace.com",
  // Instagram, Twitter and Kaggle are also supported in the links!
  // To customize icons and social links, tweak src/components/SocialMedia
  display: true // Changed from false to true to display social media links in contact section
};

// Mission Section
const missionSection = {
  title: "Making Space Sustainable",
  subTitle:
    "The CLEANS mission addresses the escalating risk of orbital debris in Low Earth Orbit (LEO). Our approach uses a spring-based tether net capture mechanism paired with an integrated drag sail to deorbit debris—all on a cost-effective 6U CubeSat platform. This scalable solution not only minimizes collision risks but also paves the way for future swarm deployments.",
  skills: [
    emoji("⚡ Engineering a robust tether net deployment system"),
    emoji("⚡ Integrating drag sail technology for efficient deorbiting"),
    emoji("⚡ Running advanced orbital simulations for precise rendezvous and capture")
  ],
  display: true // Set false to hide this section, defaults to true
};

// Concept Section

const conceptInfo = {
  display: true,
  title: "Our Concept",
  description:
    "The CLEANS mission leverages an innovative tether net and drag sail approach to remove hazardous orbital debris. By combining precise autonomous detection, agile net deployment, and controlled deorbiting via a drag sail, our CubeSat-based system is designed to significantly mitigate collision risks and promote long-term space sustainability.",
  conceptImage: require("./assets/images/concept-of-operations.png"),
  steps: [
    {
      number: 1,
      title: "LAUNCH",
      description: "Deploy the satellite into low-Earth orbit (LEO) at the designated rideshare altitude."
    },
    {
      number: 2,
      title: "RENDEZVOUS",
      description: "Adjust the satellite's orbit to match the trajectory of a selected piece of space debris."
    },
    {
      number: 3,
      title: "MATCH ORBITAL DYNAMICS",
      description: "Use ground-based telescope data and onboard sensors to track and synchronize with the debris."
    },
    {
      number: 4,
      title: "PERFORM GROUND TRACK MANEUVER",
      description: "Gradually close the distance to the debris by making precise orbital adjustments."
    },
    {
      number: 5,
      title: "DEPLOY NET",
      description: "Release the net from the cartridge to securely capture the targeted debris."
    },
    {
      number: 6,
      title: "DEPLOY DRAG SAIL",
      description: "Once the debris is secured, deploy a drag sail to slow down and initiate deorbiting."
    }
  ]
};

// WhitePaper Section
const whitepaperSection = {
  title: "White Paper",
  subtitle: "Coming Soon",
  display: true // Set false to hide this section, defaults to true
};

// Digital Twins Section
const digitalTwinsSection = {
  title: "Digital Twins",
  subtitle: "Advanced simulations for space debris characterization and removal",
  description:
    "Our digital twin technology creates high-fidelity virtual replicas of the CubeSat and its net deployment dynamics. This allows us to simulate capture operations, validate our GNC algorithms, and refine mission parameters—reducing risk and ensuring optimal performance before launch.",
  softwareSkills: [
    {
      skillName: "Python",
      fontAwesomeClassname: "fab fa-python"
    },
    {
      skillName: "MATLAB",
      fontAwesomeClassname: "fas fa-calculator"
    },
    {
      skillName: "SolidWorks",
      fontAwesomeClassname: "fas fa-cube"
    },
    {
      skillName: "STK",
      fontAwesomeClassname: "fas fa-globe"
    },
    {
      skillName: "C++",
      fontAwesomeClassname: "fab fa-cuttlefish"
    }
  ],
  gifUrl: require("./assets/images/digitaltwins.png"), 
  display: true
};

// Team Section - Replacing Achievement Section
const teamSection = {
  title: "Meet Our Team",
  subtitle:
    "The experts behind Skyfall Space's groundbreaking CLEANS mission to remove orbital debris",
  display: true,
  teamMembers: [
    {
      name: "Avik Banerjee",
      role: "Principal Investigator",
      image: require("./assets/images/team/avik.jpg"),
      bio:
        "With a strong background in aerospace engineering and mission design, Avik leads the overall strategy and technical direction of the CLEANS mission.",
      email: "",
      linkedin: "https://www.linkedin.com/in/avik-banerjee-aero/"
    },
    {
      name: "Theodore Courtois",
      role: "Project Manager",
      image: require("./assets/images/team/theodore.png"),
      bio:
        "Theodore oversees project planning, risk management, and ensures that all subteams align with mission milestones.",
      email: "",
      linkedin: "https://www.linkedin.com/in/tjcourtois/"
    },
    {
      name: "Emily Andrade",
      role: "Net Materials Lead",
      image: require("./assets/images/team/emily.png"),
      bio:
        "Emily drives the research and selection of advanced materials critical for the tether net, ensuring durability under extreme space conditions.",
      email: "",
      linkedin: "https://www.linkedin.com/in/emily-andrade8/"
    },
    {
      name: "Ben Pumphrey",
      role: "Mechanical Design Lead",
      image: require("./assets/images/team/ben.png"),
      bio:
        "Ben spearheads the mechanical design efforts, integrating a robust deployment mechanism that meets stringent SWaP constraints.",
      email: "",
      linkedin: "https://www.linkedin.com/in/ben-pumphrey/"
    },
    {
      name: "Miguel Vasquez",
      role: "Mechanical Design Engineer",
      image: require("./assets/images/team/miguel.jpg"),
      bio:
        "Miguel contributes his expertise in systems engineering and prototyping to refine the CubeSat's net deployment system.",
      email: "",
      linkedin: "https://www.linkedin.com/in/migvas/"
    },
    {
      name: "Nathan Rivera (they/them)",
      role: "Net Materials Researcher",
      image: require("./assets/images/team/nathan.png"),
      bio:
        "Nathan focuses on material testing and data analysis to optimize the net’s performance for reliable debris capture.",
      email: "",
      linkedin: "https://www.linkedin.com/in/nathanrivera22/"
    },
    {
      name: "Jamielyn Jarvis",
      role: "Net Materials Researcher",
      image: require("./assets/images/team/jamie.jpg"),
      bio:
        "Jamie leads efforts in material characterization and net design simulation, ensuring our capture system meets mission requirements.",
      email: "",
      linkedin: "https://www.linkedin.com/in/jamielyn-jarvis-39b851204/"
    },
    {
      name: "Brandon Vinh",
      role: "Mechanical Design Engineer",
      image: require("./assets/images/team/Brandon.png"),
      bio:
        "Brandon designs critical mechanical components, ensuring that the deployment system performs reliably under launch and space conditions.",
      email: "",
      linkedin: "https://www.linkedin.com/in/brandon-vinh/"
    },
    {
      name: "Abdirahman Shirwa",
      role: "Mechanical Design Engineer",
      image: require("./assets/images/team/abdi.png"),
      bio:
        "Abdi supports the mechanical integration of the CubeSat frame and deployment mechanism.",
      email: "",
      linkedin: "https://www.linkedin.com/in/abdirahman-shirwa-b8774b211/"
    },
    {
      name: "Damon Rocha",
      role: "Software Engineer",
      image: require("./assets/images/team/damon.png"),
      bio:
        "Damon develops the core software infrastructure—from physics-based simulations to advanced computer vision algorithms—ensuring precise net deployment and control.",
      email: "",
      linkedin: "https://www.linkedin.com/in/dmr4414/"
    },
    {
      name: "Jackie Edwards",
      role: "Software Analysis Lead",
      image: require("./assets/images/team/jackie.jpg"),
      bio:
        "Jackie leads the development and integration of the guidance, navigation, and control (GNC) systems essential for safe and accurate debris capture.",
      email: "",
      linkedin: "https://www.linkedin.com/in/jackie-edwards-978613235/"
    },
    {
      name: "Grant Baumann",
      role: "Business Development Lead",
      image: require("./assets/images/team/grant.png"),
      bio:
        "Grant manages the business operations of skyfall space by investigating conferences, pitch competitions, and other ways to network, fund, and scale Skyfall.",
      email: "",
      linkedin: "https://www.linkedin.com/in/grantbaumann/"
    },
    {
      name: "Jason Sanchez",
      role: "Mission Operations Lead",
      image: require("./assets/images/team/jason.png"),
      bio:
        "With 10+ years in satellite operations, RF engineering, and IT, Jason ensures mission readiness and secure communications. His expertise spans UAV research, cybersecurity, and aerospace systems integration.",
      email: "",
      linkedin: "https://www.linkedin.com/in/jason-e-sanchez/"
    }
  ]
};

const contactInfo = {
  title: "Contact Us", // Removed the emoji
  subtitle:
    "Interested in our advanced space debris removal solutions? Get in touch with our team.",
  number: "", // Removed phone number
  email_address: "info@skyfallspace.com" // Updated with Skyfall's email
};

// Twitter Section

const twitterDetails = {
  userName: "SkyfallSpace", // Replace with your Twitter username without @
  display: true // Set true to display this section, defaults to false
};

const isHireable = false; // Set false if you are not looking for a job. Also isHireable will be displayed as Open for opportunities: Yes/No in the GitHub footer

export {
  illustration,
  greeting,
  socialMediaLinks,
  splashScreen,
  missionSection,
  conceptInfo,
  whitepaperSection,
  digitalTwinsSection, 
  teamSection,
  contactInfo,
  twitterDetails,
  isHireable,
};
